/**
 * useSupportAnalytics Hook
 *
 * React hook for fetching support analytics data.
 * Handles loading states, errors, and data refresh.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 3: Analytics + Bulk Actions
 * Location: apps/web/src/hooks/support/useSupportAnalytics.js
 */

import { useState, useEffect, useCallback } from 'react';
import { supportAnalyticsApi } from '../../services/supportApi';

export function useSupportAnalytics(period = '30d') {
  const [overview, setOverview] = useState(null);
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [staffMetrics, setStaffMetrics] = useState([]);
  const [typeBreakdown, setTypeBreakdown] = useState([]);
  const [resolutionTime, setResolutionTime] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all analytics data in parallel
      const [
        overviewData,
        dailyData,
        staffData,
        typeData,
        resolutionData,
        refreshData,
      ] = await Promise.all([
        supportAnalyticsApi.getOverview({ period }),
        supportAnalyticsApi.getDailyMetrics({ period }),
        supportAnalyticsApi.getStaffMetrics(),
        supportAnalyticsApi.getTypeBreakdown(),
        supportAnalyticsApi.getResolutionTimeDistribution({ period }),
        supportAnalyticsApi.getLastRefresh(),
      ]);

      setOverview(overviewData);
      setDailyMetrics(dailyData);
      setStaffMetrics(staffData);
      setTypeBreakdown(typeData);
      setResolutionTime(resolutionData);
      setLastRefresh(refreshData.last_refresh);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [fetchAllAnalytics]);

  /**
   * Manually trigger refresh of materialized views
   */
  const manualRefresh = async () => {
    try {
      setLoading(true);
      await supportAnalyticsApi.refreshMaterializedViews();
      // Refetch all data after refresh
      await fetchAllAnalytics();
    } catch (err) {
      setError(err.message || 'Failed to refresh analytics');
      console.error('Error refreshing analytics:', err);
      throw err;
    }
  };

  return {
    overview,
    dailyMetrics,
    staffMetrics,
    typeBreakdown,
    resolutionTime,
    lastRefresh,
    loading,
    error,
    refresh: fetchAllAnalytics,
    manualRefresh,
  };
}
